import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { getStatusSnapshot, getPerformanceStats, getWebStats, getReferrers, getAnalysis } from '../../api/crm';
import AnalysisModal from '../../components/AnalysisModal';
import AdminButton from '../../components/admin/AdminButton';

const FilterButton = ({ active, label, onClick }) => (
    <AdminButton
        variant={active ? 'primary' : 'outline'}
        onClick={onClick}
        size="sm"
    >
        {label}
    </AdminButton>
);

const TrafficLight = ({ status }) => {
    const colors = {
        BLUE: 'bg-blue-100 text-blue-800 border-blue-200',
        GREEN: 'bg-green-100 text-green-800 border-green-200',
        YELLOW: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        ORANGE: 'bg-orange-100 text-orange-800 border-orange-200',
        RED: 'bg-red-100 text-red-800 border-red-200',
    };
    return colors[status] || 'bg-gray-100';
};

const Dashboard = () => {
    // State
    const [topFilter, setTopFilter] = useState('1w');
    const [webFilter, setWebFilter] = useState('1w');
    const [visitorTypeFilter, setVisitorTypeFilter] = useState('total'); // 'total', 'new', 'returning'
    const [trendUnit, setTrendUnit] = useState('day'); // 'hour', 'day', 'week', 'month'

    const [statusData, setStatusData] = useState(null);
    const [perfData, setPerfData] = useState(null);
    const [webData, setWebData] = useState(null);
    const [trendData, setTrendData] = useState([]); // Visitor Trend Data
    const [referrers, setReferrers] = useState([]);

    // eslint-disable-next-line no-unused-vars
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [loadingPerf, setLoadingPerf] = useState(true);
    const [loadingWeb, setLoadingWeb] = useState(true);

    const [modalOpen, setModalOpen] = useState(false); // Analysis Modal
    // const [refModalOpen, setRefModalOpen] = useState(false); // Referrer Full Modal (Optional)

    // Initial Load & Effects
    useEffect(() => { loadStatus(); }, []);
    useEffect(() => { loadPerformance(); }, [topFilter]);
    useEffect(() => {
        // Auto-switch Trend Unit based on Period
        if (webFilter === 'TD') setTrendUnit('hour');
        else if (webFilter === '1w') setTrendUnit('day');
        else if (['1m', '3m'].includes(webFilter)) setTrendUnit('week');
        else setTrendUnit('month');

        loadWebStats();
        loadReferrers();
        // loadVisitorTrend will rely on the updated trendUnit, but state update is async.
        // We will call it in a separate effect or here if we trust the closure, 
        // but better to split or pass args. passing args is safer.
        // Actually, let's trigger loadVisitorTrend in a separate effect dependent on webFilter AND trendUnit?
        // No, infinite loop risk if not careful.
        // Let's pass the 'new unit' directly to load function.
    }, [webFilter]);

    useEffect(() => {
        loadVisitorTrend();
    }, [webFilter, trendUnit]); // Reload when Filter OR Unit changes

    // loaders
    const loadStatus = async () => {
        try {
            const data = await getStatusSnapshot();
            setStatusData(data);
        } catch (e) { console.error(e); } finally { setLoadingStatus(false); }
    };

    const loadPerformance = async () => {
        setLoadingPerf(true);
        try {
            const data = await getPerformanceStats(topFilter);
            setPerfData(data);
        } catch (e) { console.error(e); } finally { setLoadingPerf(false); }
    };

    const loadWebStats = async () => {
        setLoadingWeb(true);
        try {
            const data = await getWebStats(webFilter);
            setWebData(data);
        } catch (e) { console.error(e); } finally { setLoadingWeb(false); }
    };

    const loadVisitorTrend = async () => {
        try {
            const data = await getAnalysis('business', webFilter, trendUnit); // Pass unit
            setTrendData(data);
        } catch (e) { console.error(e); }
    };

    const loadReferrers = async () => {
        try {
            const data = await getReferrers(webFilter, 10);
            setReferrers(data.data);
        } catch (e) { console.error(e); }
    };

    const formatTime = (ms) => {
        if (!ms) return '0분';
        const mins = Math.round(ms / 60000);
        if (mins < 60) return `${mins}분`;
        return `${(mins / 60).toFixed(1)}시간`;
    };

    // Chart Render Helper
    const renderTrendChart = () => {
        if (!trendData || trendData.length === 0) return <div className="p-10 text-center text-gray-400 font-bold">No Data</div>;

        const maxValue = Math.max(...trendData.map(d =>
            visitorTypeFilter === 'total' ? (d.total || 0) : visitorTypeFilter === 'new' ? (d.new || 0) : (d.returning || 0)
        )) || 1;

        return (
            <div className="w-full overflow-x-auto pb-4 mt-8">
                <div className="flex justify-between items-end min-w-[600px] md:min-w-[400px] h-56 gap-2 px-2 border-b border-gray-100">
                    {trendData.map((item, idx) => {
                        const val = visitorTypeFilter === 'total' ? (item.total || 0) : visitorTypeFilter === 'new' ? (item.new || 0) : (item.returning || 0);
                        const height = (val / maxValue) * 100;
                        const dateLabel = item.label || (item.date ? item.date.slice(5) : '-');
                        
                        // Select color logically
                        let barColor = 'bg-primary-500';
                        if (visitorTypeFilter === 'total') barColor = 'bg-indigo-900';
                        else if (visitorTypeFilter === 'returning') barColor = 'bg-emerald-500';

                        return (
                            <div key={idx} className="flex flex-col items-center justify-end h-full group flex-1" title={`${item.date || dateLabel}: ${val}명`}>
                                {/* Value Label (Hover/Always visible conditionally) */}
                                <span className={`text-xs font-bold mb-2 transition-opacity duration-200 ${val > 0 ? 'text-gray-700' : 'text-transparent'}`}>
                                    {val}
                                </span>
                                
                                {/* Bar Track */}
                                <div className="w-full max-w-[40px] flex-1 bg-gray-50 rounded-t-lg relative flex flex-col justify-end overflow-hidden border-x border-t border-gray-100">
                                    {/* Filled Amount */}
                                    <div
                                        className={`w-full ${barColor} shadow-inner rounded-t-sm transition-all duration-700 hover:brightness-110 ease-[cubic-bezier(0.4,0,0.2,1)]`}
                                        style={{ height: `${height}%`, minHeight: val > 0 ? '4px' : '0' }}
                                    ></div>
                                </div>
                                
                                {/* X-Axis Date */}
                                <span className="text-[11px] font-semibold text-gray-400 mt-3 truncate whitespace-nowrap">
                                    {dateLabel}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <AdminLayout>
            <div className="dashboard-container">
                <div className="dashboard-header">
                    <h2>통계 및 대시보드</h2>
                </div>

                {/* --- TOP SECTION (Status + Performance) --- */}
                <div className="section-container card-group-bounded mt-4 md:mt-0">
                    <div className="filters-row flex flex-wrap gap-2 items-center mb-4">
                        <span className="filter-label">Performance Filter:</span>
                        {['TD', '1w', '1m', '3m', '6m', '1y'].map(p => (
                            <FilterButton key={p} label={p.toUpperCase()} active={topFilter === p} onClick={() => setTopFilter(p)} />
                        ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
                        {/* 1. Status Snapshot (No Filter) */}
                        <div className="kpi-card status-card bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-center">
                            <h4 className="text-sm font-semibold text-gray-500 mb-3 m-0">
                                <span className="text-red-500">신규</span> / <span className="text-orange-500">조치</span> / <span className="text-yellow-600">대기</span>
                            </h4>

                            {!statusData ? <div>Loading...</div> : (
                                <>
                                    <div className="text-3xl font-bold flex items-center gap-2 flex-wrap text-gray-900">
                                        <span className="text-red-500">{statusData.unprocessed.count}</span>
                                        <span className="text-gray-300 font-light">/</span>
                                        <span className="text-orange-500">{statusData.actionRequired.count}</span>
                                        <span className="text-gray-300 font-light">/</span>
                                        <span className="text-yellow-600">{statusData.waitingForResponse.count}</span>
                                    </div>
                                    <div className="delay-row mt-4 pt-3 border-t border-dashed border-gray-200">
                                        <div className="text-xs text-gray-400 mb-1">1주 이상 지연 건</div>
                                        <div className="text-sm font-semibold flex items-center gap-2">
                                            <span className="text-red-400">{statusData.unprocessed.delayed}</span>
                                            <span className="text-gray-300">/</span>
                                            <span className="text-orange-400">{statusData.actionRequired.delayed}</span>
                                            <span className="text-gray-300">/</span>
                                            <span className="text-yellow-500">{statusData.waitingForResponse.delayed}</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* 2. Lead Time (Filtered) */}
                        {loadingPerf || !perfData ? <div className="kpi-card p-5 border rounded-xl">Loading...</div> : (
                            <div className={`kpi-card p-5 rounded-xl border flex flex-col justify-center ${TrafficLight({ status: perfData.leadTime.status })}`}>
                                <h4 className="text-sm font-semibold opacity-80 mb-3 m-0">평균 응답 시간</h4>
                                <div className="text-3xl font-bold mb-2">{formatTime(perfData.leadTime.current)}</div>
                                <div className="text-xs opacity-70">
                                    전주 대비 <span className={`font-bold ${perfData.leadTime.current < perfData.leadTime.previous ? 'text-blue-600' : 'text-red-600'}`}>
                                        {perfData.leadTime.current < perfData.leadTime.previous ? '▼' : '▲'}
                                    </span>
                                    ({formatTime(perfData.leadTime.previous)})
                                </div>
                            </div>
                        )}

                        {/* 3. Completed (Filtered) */}
                        {loadingPerf || !perfData ? <div className="kpi-card p-5 border rounded-xl">Loading...</div> : (
                            <div className="kpi-card p-5 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col justify-center">
                                <h4 className="text-sm font-semibold text-gray-500 mb-3 m-0">완료된 문의</h4>
                                <div className="text-3xl font-bold text-emerald-600 mb-2">{perfData.completed} <span className="text-sm font-normal text-gray-500">건</span></div>
                                <div className="text-xs text-gray-500">리뷰 요청: {perfData.reviewRequested}건</div>
                            </div>
                        )}
                    </div>
                </div>


                {/* --- BOTTOM SECTION (Web Stats) --- */}
                <div className="section-container mt-6">
                    <div className="header-with-filter flex flex-col md:flex-row justify-between md:items-center gap-4 border-b pb-4 mb-4">
                        <h3 className="text-lg font-bold m-0">웹사이트 유입 분석</h3>
                        <div className="filters-row flex flex-wrap gap-2 m-0">
                            {['TD', '1w', '1m', '3m', '6m', '1y'].map(p => (
                                <FilterButton key={p} label={p.toUpperCase()} active={webFilter === p} onClick={() => setWebFilter(p)} />
                            ))}
                        </div>
                    </div>

                    {loadingWeb || !webData ? <div>Loading Web Stats...</div> : (
                        <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr_280px] gap-5 w-full">
                            {/* 1. KPI Card */}
                            <div className="stat-box visitor-kpi flex-1 xl:max-w-[300px] bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col justify-center">
                                <h4 className="text-sm font-semibold text-gray-500 mb-4 m-0">순방문 / 재방문</h4>
                                <div className="text-3xl font-extrabold text-gray-900 mb-3 flex items-baseline gap-2">
                                    {webData.uv} <span className="text-lg text-gray-400 font-medium">/ {webData.returningVisitors}</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-2 pt-4 border-t border-gray-200">PV: {webData.pv} | Clicks: {webData.clicks}</div>
                            </div>

                            {/* 2. Visitor Trend Chart (Center) */}
                            <div className="visitor-chart-container bg-white border border-gray-200 rounded-xl p-5 w-full min-w-0">
                                <div className="chart-header flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-5">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <h4 className="m-0 font-bold text-gray-700">방문자 추이</h4>
                                        <div className="unit-toggles flex flex-wrap gap-1">
                                            {['hour', 'day', 'week', 'month'].map(u => (
                                                <AdminButton
                                                    key={u}
                                                    variant={trendUnit === u ? 'primary' : 'outline'}
                                                    size="sm"
                                                    onClick={() => setTrendUnit(u)}
                                                >
                                                    {u === 'hour' ? '시간' : u === 'day' ? '일' : u === 'week' ? '주' : '월'}
                                                </AdminButton>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="chart-toggles flex flex-wrap gap-1 bg-gray-100 p-1 rounded-md">
                                        <AdminButton variant={visitorTypeFilter === 'total' ? 'secondary' : 'ghost'} size="sm" onClick={() => setVisitorTypeFilter('total')}>전체</AdminButton>
                                        <AdminButton variant={visitorTypeFilter === 'new' ? 'secondary' : 'ghost'} size="sm" onClick={() => setVisitorTypeFilter('new')}>순방문</AdminButton>
                                        <AdminButton variant={visitorTypeFilter === 'returning' ? 'secondary' : 'ghost'} size="sm" onClick={() => setVisitorTypeFilter('returning')}>재방문</AdminButton>
                                    </div>
                                </div>
                                {renderTrendChart()}
                            </div>

                            {/* 3. Referrer List (Right most) */}
                            <div className="referrer-list-container flex-1 xl:max-w-[300px] bg-white border border-gray-200 rounded-xl p-5 flex flex-col">
                                <h4 className="text-sm font-semibold text-gray-700 m-0 mb-3">유입 사이트 Top 10</h4>
                                <ul className="referrer-list max-h-60 overflow-y-auto m-0 p-0 flex-1">
                                    {referrers.map((ref, idx) => (
                                        <li key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0 text-sm">
                                            <span className="text-gray-600 font-medium truncate pr-2">{ref.name}</span>
                                            <span className="text-gray-400 shrink-0">{ref.count}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>

                {/* --- Performance Bar (Collapsible) --- */}
                <div className="performance-bar mt-6 flex justify-center pb-8">
                    <AdminButton variant="outline" size="lg" onClick={() => setModalOpen(true)}>
                        🚀 퍼포먼스 분석 (Page / User / Field) ▲
                    </AdminButton>
                </div>

                <AnalysisModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    period={webFilter} // Reuse web filter for sync consistency
                />
            </div>
        </AdminLayout>
    );
};

export default Dashboard;
